import { useCallback, useEffect, useState } from "react";
import { CategoryDropdown } from "./CategoryDropdown";
import { useFacilityNotes } from "./useFacilityNotes";
import produce from "immer";
import { FacilityNote } from "./FacilityNote";
import { FacilityCategoryNote } from "src/api/facility";

const notesToHide = ["HCP Check-In Instructions (visible to worker)"];

interface FacilityNotesProps {
  facilityId: string;
}

export function FacilityNotes({ facilityId }: FacilityNotesProps) {
  const [categoriesVisible, setCategoriesVisible] = useState<Record<string, boolean>>({});
  const {
    data: facilityNotes,
    updateFacilityNotes,
    invalidateCache: invalidateFacilityNotesCache,
  } = useFacilityNotes(facilityId, {
    onDataChanged: useCallback((data) => {
      setCategoriesVisible((prevState) =>
        produce(prevState, (draft) => {
          data.forEach((facilityNote) => {
            draft[facilityNote.categoryId] = draft[facilityNote.categoryId] ?? true;
          });
        })
      );
    }, []),
  });

  const updateCategoryVisible = (categoryId: string, checked: boolean) => {
    setCategoriesVisible((prevState) => ({ ...prevState, [categoryId]: checked }));
  };

  const makeAllCategoriesVisible = (visible: boolean) => {
    setCategoriesVisible((prevState) =>
      produce(prevState, (draft) => {
        facilityNotes.forEach((facilityNote) => {
          draft[facilityNote.categoryId] = visible;
        });
      })
    );
  };

  const updateFacilityNote = (updatedFacilityNote: FacilityCategoryNote) => {
    updateFacilityNotes(
      facilityNotes.map((facilityNote) =>
        facilityNote.categoryId === updatedFacilityNote.categoryId
          ? updatedFacilityNote
          : facilityNote
      )
    );
  };

  useEffect(() => {
    return () => {
      invalidateFacilityNotesCache();
    };
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <CategoryDropdown
        facilityNotes={facilityNotes}
        categoriesVisible={categoriesVisible}
        updateCategoryVisible={updateCategoryVisible}
        onClearAll={() => makeAllCategoriesVisible(false)}
        onSelectAll={() => makeAllCategoriesVisible(true)}
      />
      <div className="notes-container">
        {facilityNotes
          .filter((facilityNote) => categoriesVisible[facilityNote.categoryId])
          .map((facilityNote) => (
            <FacilityNote
              key={facilityNote.categoryId}
              facilityNote={facilityNote}
              facilityId={facilityId}
              hideContent={notesToHide.includes(facilityNote.category)}
              onUpdateFacilityNote={updateFacilityNote}
            />
          ))}
      </div>
    </>
  );
}
